import React from "react";
import { Search, MapPin, ChevronDown, X, Globe } from "lucide-react";

import ilceKoyData from "../json/ilce-koyler.json";
import ilIlceData from "../json/il-ilceler.json";
import ulkeSehirData from "../json/ulke-sehir.json";

/* ---------- Ülkeler (Türkiye başta) ---------- */
const COUNTRIES = (() => {
  const uniqueCountries = [...new Set(ulkeSehirData.map((item) => item.ulke))];
  const others = uniqueCountries
    .filter((c) => c !== "Türkiye")
    .sort((a, b) => a.localeCompare(b, "tr"));
  return ["Türkiye", ...others];
})();

/* ---------- Türkiye'nin 81 İli (Öncelikli iller başta) ---------- */
const PRIORITY_CITIES = ["İstanbul", "Sivas", "Ankara", "İzmir"];

const TURKEY_CITIES = (() => {
  const allCities = ilIlceData.map((il) => il.text);

  const priority = allCities
    .filter((city) =>
      PRIORITY_CITIES.some(
        (p) => p.toLocaleLowerCase("tr") === city.toLocaleLowerCase("tr")
      )
    )
    .sort((a, b) => {
      const aIndex = PRIORITY_CITIES.findIndex(
        (p) => p.toLocaleLowerCase("tr") === a.toLocaleLowerCase("tr")
      );
      const bIndex = PRIORITY_CITIES.findIndex(
        (p) => p.toLocaleLowerCase("tr") === b.toLocaleLowerCase("tr")
      );
      return aIndex - bIndex;
    });

  const others = allCities
    .filter((city) => !priority.includes(city))
    .sort((a, b) => a.localeCompare(b, "tr"));

  return [...priority, ...others];
})();

/* --- helper'lar --- */
const toTitleTR = (s = "") =>
  s
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w ? w[0].toLocaleUpperCase("tr") + w.slice(1) : ""))
    .join(" ");

const normTR = (s = "") =>
  s
    .toString()
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    .trim();

const HeroSection = ({ setCurrentPage }) => {
  const [kw, setKw] = React.useState("");
  const [country, setCountry] = React.useState("Türkiye");
  const [city, setCity] = React.useState("");
  const [cityDistrict, setCityDistrict] = React.useState("");
  const [selectedIlce, setSelectedIlce] = React.useState("");
  const [selectedKoy, setSelectedKoy] = React.useState("");

  // Köy arama state'leri
  const [koySearch, setKoySearch] = React.useState("");
  const [isKoyDropdownOpen, setIsKoyDropdownOpen] = React.useState(false);
  const koyDropdownRef = React.useRef(null);

  // Türkiye seçili mi?
  const isTurkiye = country === "Türkiye";

  // Seçilen ile göre ilçeleri al (il-ilceler.json'dan) - SADECE TÜRKİYE İÇİN
  const cityDistricts = React.useMemo(() => {
    if (!city || !isTurkiye) return [];
    const ilData = ilIlceData.find((il) => normTR(il.text) === normTR(city));
    if (!ilData) return [];
    return ilData.districts
      .map((d) => d.text)
      .sort((a, b) => a.localeCompare(b, "tr"));
  }, [city, isTurkiye]);

  // Benzersiz ilçeleri al (ilce-koyler.json'dan - hemşehri için) - TÜM ÜLKELER İÇİN
  const ilceler = React.useMemo(() => {
    const uniqueIlce = [...new Set(ilceKoyData.map((item) => item.ilce))];
    return uniqueIlce.sort((a, b) => a.localeCompare(b, "tr"));
  }, []);

  // Seçilen ilçeye göre köyleri filtrele - TÜM ÜLKELER İÇİN
  const koyler = React.useMemo(() => {
    if (!selectedIlce) return [];
    const filteredKoy = ilceKoyData
      .filter((item) => normTR(item.ilce) === normTR(selectedIlce))
      .map((item) => item.koy);

    // "Merkez" içerenleri başa al, sonra alfabetik sırala
    return filteredKoy.sort((a, b) => {
      const aMerkez = normTR(a).includes("merkez");
      const bMerkez = normTR(b).includes("merkez");
      if (aMerkez && !bMerkez) return -1;
      if (!aMerkez && bMerkez) return 1;
      return a.localeCompare(b, "tr");
    });
  }, [selectedIlce]);

  // Arama yapılmış köy listesi
  const filteredKoyler = React.useMemo(() => {
    if (!koySearch) return koyler;
    const searchTerm = normTR(koySearch);
    return koyler.filter((koy) => normTR(koy).includes(searchTerm));
  }, [koyler, koySearch]);

  // Ülke değiştiğinde sadece il ve il ilçesini sıfırla (hemşehri ilçe/köy kalsın)
  React.useEffect(() => {
    setCity("");
    setCityDistrict("");
  }, [country]);

  // İl değiştiğinde ilçeyi sıfırla
  React.useEffect(() => {
    setCityDistrict("");
  }, [city]);

  // İlçe değiştiğinde köyü sıfırla
  React.useEffect(() => {
    setSelectedKoy("");
    setKoySearch("");
  }, [selectedIlce]);

  // Dropdown dışına tıklanınca kapat
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        koyDropdownRef.current &&
        !koyDropdownRef.current.contains(event.target)
      ) {
        setIsKoyDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <section className="relative w-full" id="hero-bg">
      {/* Arka plan + overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=1600&q=80')`,
          backgroundAttachment: "fixed",
        }}
      />
      <div className="absolute inset-0 bg-black/45" />

      {/* İçerik */}
      <div
        className="
          relative z-10
          min-h-[60svh] md:min-h-[50vh] lg:min-h-[42vh]
          flex items-center justify-center
          px-4
          pt-[env(safe-area-inset-top)]
        "
      >
        <div className="w-full max-w-7xl text-white text-center">
          <h1 className="text-balance text-3xl md:text-4xl font-bold mb-5">
            Hemşehrinizden neyi, nerede almak istiyorsanız tek tıkla ulaşın.
          </h1>

          {/* Arama Formu */}
          <form
            className="w-full mx-auto grid grid-cols-1 lg:grid-cols-6 gap-2 md:gap-4"
            onSubmit={(e) => {
              e.preventDefault();

              // Filtreleri FirmsPage'e aktar
              sessionStorage.setItem("pref:search", kw);
              sessionStorage.setItem("pref:country", country);
              sessionStorage.setItem("pref:city", city);
              sessionStorage.setItem("pref:cityDistrict", cityDistrict);
              sessionStorage.setItem("pref:district", selectedIlce);
              sessionStorage.setItem("pref:village", selectedKoy);

              setCurrentPage("firms");
            }}
          >
            {/* Kelime */}
            <div className="flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
              <Search className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
              <input
                type="text"
                value={kw}
                onChange={(e) => setKw(e.target.value)}
                className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] placeholder:text-[12px] md:placeholder:text-[14px]"
                placeholder="Ne arıyorsunuz ?"
              />
            </div>

            {/* Ülke Dropdown */}
            <div className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
              <Globe className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
              <div className="min-w-0 w-full relative">
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent appearance-none cursor-pointer"
                >
                  {COUNTRIES.map((ulke) => (
                    <option key={ulke} value={ulke}>
                      {ulke}
                    </option>
                  ))}
                </select>
              </div>
              <ChevronDown className="text-gray-400 w-4 h-4 shrink-0 pointer-events-none" />
            </div>

            {/* İl Dropdown (Sadece Türkiye için aktif) */}
            <div className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
              <MapPin className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
              <div className="min-w-0 w-full relative">
                {!city && (
                  <span className="absolute inset-0 flex items-center text-gray-400 text-[12px] md:text-[14px] pointer-events-none">
                    {isTurkiye ? "İl seçiniz..." : "Sadece Türkiye"}
                  </span>
                )}
                <select
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  disabled={!isTurkiye}
                  className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent appearance-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400"
                >
                  <option value=""></option>
                  {isTurkiye &&
                    TURKEY_CITIES.map((il) => (
                      <option key={il} value={il}>
                        {il}
                      </option>
                    ))}
                </select>
              </div>
              <ChevronDown className="text-gray-400 w-4 h-4 shrink-0 pointer-events-none" />
            </div>

            {/* İl İçin İlçe Dropdown (Sadece Türkiye + il seçiliyse) */}
            <div className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
              <ChevronDown className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
              <div className="min-w-0 w-full relative">
                {!cityDistrict && (
                  <span className="absolute inset-0 flex items-center text-gray-400 text-[12px] md:text-[14px] pointer-events-none">
                    {!isTurkiye
                      ? "Sadece Türkiye"
                      : city
                      ? "İlçe seçiniz..."
                      : "Önce il seçiniz..."}
                  </span>
                )}
                <select
                  value={cityDistrict}
                  onChange={(e) => setCityDistrict(e.target.value)}
                  disabled={!city || !isTurkiye}
                  className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent appearance-none cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400"
                >
                  <option value=""></option>
                  {cityDistricts.map((ilce) => (
                    <option key={ilce} value={ilce}>
                      {toTitleTR(ilce)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Hemşehri İlçe Dropdown (Tüm ülkeler için aktif) */}
            <div className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5">
              <ChevronDown className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
              <div className="min-w-0 w-full relative">
                {!selectedIlce && (
                  <span className="absolute inset-0 flex items-center text-gray-400 text-[12px] md:text-[14px] pointer-events-none">
                    Hemşehriniz hangi ilçeden olsun ?
                  </span>
                )}
                <select
                  value={selectedIlce}
                  onChange={(e) => setSelectedIlce(e.target.value)}
                  className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent appearance-none cursor-pointer"
                >
                  <option value=""></option>
                  {ilceler.map((ilce) => (
                    <option key={ilce} value={ilce}>
                      {toTitleTR(ilce)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Köy Dropdown (Tüm ülkeler için aktif) */}
            <div
              ref={koyDropdownRef}
              className="relative flex items-center bg-white rounded-xl shadow-lg h-14 md:h-16 px-4 md:px-5"
            >
              <Search className="text-gray-400 w-5 h-5 mr-3 shrink-0" />
              <div className="min-w-0 w-full relative">
                <input
                  type="text"
                  value={selectedKoy ? toTitleTR(selectedKoy) : koySearch}
                  onChange={(e) => {
                    setKoySearch(e.target.value);
                    setSelectedKoy("");
                    setIsKoyDropdownOpen(true);
                  }}
                  onFocus={() => setIsKoyDropdownOpen(true)}
                  disabled={!selectedIlce}
                  placeholder={
                    selectedIlce
                      ? "Hemşehri Köy ara..."
                      : "Önce hemşehri ilçe seçiniz..."
                  }
                  className="min-w-0 w-full outline-none text-gray-800 text-[14px] md:text-[16px] bg-transparent cursor-pointer disabled:text-gray-400 disabled:cursor-not-allowed placeholder:text-[12px] md:placeholder:text-[14px]"
                />

                {/* Temizle butonu */}
                {(selectedKoy || koySearch) && selectedIlce && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedKoy("");
                      setKoySearch("");
                      setIsKoyDropdownOpen(true);
                    }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}

                {/* Dropdown listesi */}
                {isKoyDropdownOpen && selectedIlce && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl max-h-60 overflow-y-auto z-50 border border-gray-200">
                    {filteredKoyler.length > 0 ? (
                      filteredKoyler.map((koy) => (
                        <div
                          key={koy}
                          onClick={() => {
                            setSelectedKoy(koy);
                            setKoySearch("");
                            setIsKoyDropdownOpen(false);
                          }}
                          className="px-4 py-2.5 hover:bg-gray-100 cursor-pointer text-gray-800 text-[14px] md:text-[16px] text-left transition"
                        >
                          {toTitleTR(koy)}
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2.5 text-gray-400 text-[14px] text-left">
                        Sonuç bulunamadı
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Buton: Alt sırada ortalı */}
            <div className="col-span-full flex justify-center">
              <button
                type="submit"
                className="bg-amber-400 text-gray-900 font-bold rounded-xl shadow-lg h-12 md:h-14 px-7 md:px-8 hover:bg-amber-500 transition"
              >
                ARAMA YAP
              </button>
            </div>
          </form>

          <div className="mt-3 text-xs md:text-sm text-white/90">
            İpucu: Enter'a basarak da arama yapabilirsiniz.
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
